import { useEffect, useState } from 'react'
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useResolvedPath
} from 'react-router-dom'
import { startCase } from 'lodash-es'

import { QRADatasetRowEntry, QRARowEntryCRID } from '$/types'
import { useAppSelector } from 'app/hooks'
import {
  IGetQRATableQueryParams,
  useAddQRATableExportJobMutation,
  useGetQRATableQuery
} from 'app/services/api/qra/datatable'
import {
  IGetQRAGraphsQueryParams,
  IQRAGraphCsvExportParams
} from 'app/services/api/qra/graphs'
import { selectSystemScopeIdContainer } from 'app/layout/Navbar/Filters/globalFilterSelectors'
import OverlayScrollbar from 'common/components/OverlayScrollBar'
import { useToggleRiskDensity } from 'common/hooks/useToggleRiskDensity'
import {
  IQRANavUtilsConsumerComponentInitProps,
  QRAViewType
} from 'common/utils/navigation/qraNavUtils'

import { useQraGraphOverride } from '_/src/features/RiskManagement/Components/Graphs/useQraGraphOverride'
import { IQRAViewProps } from 'features/RiskManagement'
import {
  IGraphsViewBaseProps,
  IQRAGraphBools
} from 'features/RiskManagement/Components/Graphs'
import GraphsView from 'features/RiskManagement/Components/Graphs/Layout/SystemView'
import {
  selectRiskUnit,
  selectSavedFilter
} from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'
import RiskViewShell from 'features/RiskManagement/Components/RiskViewShell'
import { IGroupColumnsSelection } from 'features/RiskManagement/Components/Tables'
import TableView from 'features/RiskManagement/Components/Tables/Layout/SystemView'
import { convertTableAPIDataToQRATableComponentProps } from 'features/RiskManagement/Components/Tables/utils/data'
import MapWrapper from 'features/RiskManagement/QRA/Components/MapWrapper'

import QRASaveStateButton from './Components/Buttons/QRASaveStateButton'
import CurrentStateToolbar from './Components/Toolbar/CurrentStateToolbar'

interface IViewWrapperProps {
  qraBasePath: string
  url: string
}

const GraphsViewWrapper = ({ qraBasePath, url }: IViewWrapperProps) => (
  <div className='flex flex-1'>
    <OverlayScrollbar>
      <SystemGraphsInit qraBasePath={qraBasePath} qraViewBasePath={url} />
    </OverlayScrollbar>
    <div className='w-1/4'>
      <MapWrapper baselineId='current' />
    </div>
  </div>
)

const TableViewWrapper = ({ qraBasePath, url }: IViewWrapperProps) => (
  <SystemTableInit qraBasePath={qraBasePath} qraViewBasePath={url} />
)

// #region System View Main
const SystemView = ({ qraBasePath }: IQRAViewProps) => {
  const { pathname } = useResolvedPath('')

  const pageHeaderText = 'Current State Risk'
  const pageSubheading = 'System View'

  return (
    <RiskViewShell
      breadcrumbs={pageSubheading}
      title={pageHeaderText}
      actions={<QRASaveStateButton />}
      toolbar={
        <CurrentStateToolbar
          qraBasePath={qraBasePath}
          qraViewBasePath={pathname}
        />
      }
    >
      <div className='flex flex-1 overflow-auto bg-gray-100'>
        <Routes>
          <Route
            path={QRAViewType.Graph}
            element={
              <GraphsViewWrapper qraBasePath={qraBasePath} url={pathname} />
            }
          />
          <Route
            path={QRAViewType.Table}
            element={
              <TableViewWrapper qraBasePath={qraBasePath} url={pathname} />
            }
          />
          <Route
            path='*'
            element={
              <GraphsViewWrapper qraBasePath={qraBasePath} url={pathname} />
            }
          />
        </Routes>
      </div>
    </RiskViewShell>
  )
}
// #endregion

// #region System Graphs View
const SystemGraphsInit = ({
  qraBasePath,
  qraViewBasePath
}: IGraphsViewBaseProps) => {
  const activeFilterProfileIdContainer = useAppSelector(
    selectSystemScopeIdContainer
  )

  const localFilter = useAppSelector(selectSavedFilter)
  const riskUnit = useAppSelector(selectRiskUnit)

  const graphSelections: IQRAGraphBools = {
    riskbyconsequence: true,
    riskbyline: true,
    riskforecast: true
  }

  const shouldSkipGraphs = activeFilterProfileIdContainer.id == null

  const qraGraphsAPIParams: IGetQRAGraphsQueryParams = {
    systemScopeId: activeFilterProfileIdContainer.id?.toString() ?? '',
    localFilter,
    riskUnit,
    graphSelections
  }

  const {
    charts,
    riskByPipelineLengthGraphData,
    riskBySystemGraphData,
    isFetching,
    graphLoadingByType
  } = useQraGraphOverride({
    graphsQueryParams: qraGraphsAPIParams,
    skip: shouldSkipGraphs,
    refetchOn: activeFilterProfileIdContainer
  })

  const graphCsvExportParams: IQRAGraphCsvExportParams = {
    systemScopeId: activeFilterProfileIdContainer.id?.toString() ?? '',
    localFilter,
    riskUnit
  }

  return (
    <GraphsView
      qraBasePath={qraBasePath}
      qraViewBasePath={qraViewBasePath}
      showLoading={isFetching}
      charts={charts}
      riskByPipelineLengthGraphData={riskByPipelineLengthGraphData}
      riskBySystemGraphData={riskBySystemGraphData}
      hasLineViewNavigation
      graphLoadingByType={graphLoadingByType}
      csvExportParams={graphCsvExportParams}
    />
  )
}
// #endregion

// #region System Table View
const SystemTableInit = ({
  qraBasePath
}: IQRANavUtilsConsumerComponentInitProps) => {
  useToggleRiskDensity()

  const navigate = useNavigate()
  const { search } = useLocation()

  const activeFilterProfileIdContainer = useAppSelector(
    selectSystemScopeIdContainer
  )

  const localFilter = useAppSelector(selectSavedFilter)
  const riskUnit = useAppSelector(selectRiskUnit)

  const [groupColumns, setGroupColumns] = useState<IGroupColumnsSelection[]>([
    { id: 0, name: 'Threat', groupTypeColumns: undefined }
  ])

  const qraTableQueryParams: IGetQRATableQueryParams = {
    systemScopeId: activeFilterProfileIdContainer.id?.toString() ?? '',
    localFilter,
    riskUnit,
    tableTypeSelections: groupColumns.map((group) => startCase(group.name))
  }

  const {
    data: tableData,
    isFetching,
    isUninitialized,
    refetch
  } = useGetQRATableQuery(qraTableQueryParams, {
    skip: activeFilterProfileIdContainer.id == null
  })

  const [addQRATableExportJob] = useAddQRATableExportJobMutation()

  useEffect(() => {
    if (!isUninitialized) {
      refetch()
    }
  }, [activeFilterProfileIdContainer, isUninitialized, refetch])

  const rowSelectCB = (row: QRADatasetRowEntry<QRARowEntryCRID>): void => {
    navigate(
      `${qraBasePath}/line/${row.ContinuousRouteId}/${QRAViewType.Table}${search}`
    )
  }

  const [colDefs, rowData] =
    convertTableAPIDataToQRATableComponentProps<QRARowEntryCRID>(
      tableData,
      groupColumns
    )

  return (
    <TableView
      showLoading={isFetching}
      colDefs={colDefs.map((colDef) => ({
        ...colDef,
        sortable: true
      }))}
      rowData={rowData}
      groupColumns={groupColumns}
      setGroupColumns={setGroupColumns}
      rowSelectCB={rowSelectCB}
      mutationTrigger={addQRATableExportJob}
      exportParams={qraTableQueryParams}
    />
  )
}
// #endregion

export default SystemView
